import React, { useState, useEffect, useRef } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import { AgGridReact } from 'ag-grid-react';
import { toast } from 'react-toastify';
import { Mail, Search, Settings, Trash2 } from 'lucide-react';

import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import ParticleService from '../../ParticleService';


// Import the new Manager Modal
import DeviceManagerModal from '../Modals/DeviceManagerModal';

export default function UserTable() {
  // Modal States
  const [deleteModalShow, setDeleteModalShow] = useState(false);
  const [manageModalShow, setManageModalShow] = useState(false);
  
  // Data States
  const [rowData, setRowData] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null); // User selected for Deletion OR Management
  const [resendingUser, setResendingUser] = useState(null);
  
  // Grid State
  const gridRef = useRef();
  const [quickFilterText, setQuickFilterText] = useState('');

  useEffect(() => {
    fetchTableData();
    const handler = () => fetchTableData();
    document.addEventListener('dataRefresh', handler);
    return () => document.removeEventListener('dataRefresh', handler);
  }, []);

  async function fetchTableData() {
    try {
        const [LTEUsers, WIFIUsers] = await Promise.all([
            ParticleService.getLTEUsers().catch(() => []),
            ParticleService.getWIFIUsers().catch(() => [])
        ]);

        let rowList = [];
        if (LTEUsers) {
          LTEUsers.forEach(obj => {
            let row = { ...obj, product: "LTE" };
            rowList.push(row);
          });
        } 
        if (WIFIUsers) {
          WIFIUsers.forEach(obj => {
            let row = { ...obj, product: "WIFI" };
            rowList.push(row);
          });
        } 
        setRowData(rowList);
    } catch (e) {
        console.error("Error fetching user table data", e);
    }
  }

  // --- Handlers ---

  const handleDeleteClick = (user) => {
      setSelectedUser(user);
      setDeleteModalShow(true);
  };

  const handleManageClick = (user) => {
      setSelectedUser(user); // Set the user to be passed to the modal
      setManageModalShow(true);
  };

  const handleResendWelcome = async user => {
      setResendingUser(user.username);
      try {
          const result = await ParticleService.resendWelcomeEmail(user.username);
          if (result.error) toast.error(result.detail || result.error);
          else toast.success(`Welcome and password setup email sent to ${user.username}.`);
      } catch (error) {
          console.error(error);
          toast.error('The welcome email could not be sent.');
      } finally {
          setResendingUser(null);
      }
  };

  const onFilterTextChange = (e) => {
      setQuickFilterText(e.target.value);
      gridRef.current.api.setQuickFilter(e.target.value);
  };

  // --- Cell Renderers ---

  const ActionButtons = (props) => {
    return (
      <div className="d-flex gap-2 py-1">
        <Button 
            variant="outline-primary" 
            size="sm" 
            className="d-flex align-items-center"
            onClick={() => handleManageClick(props.data)}
            title="View and assign devices"
        >
            <Settings size={14} className="me-1" /> Manage
        </Button>

        <Button
            variant="outline-info"
            size="sm"
            className="d-flex align-items-center"
            disabled={resendingUser === props.data.username}
            onClick={() => handleResendWelcome(props.data)}
            title="Resend the welcome email with a one-time password setup code"
        >
            <Mail size={14} className="me-1" /> {resendingUser === props.data.username ? 'Sending…' : 'Resend setup'}
        </Button>

        <Button 
            variant="outline-danger" 
            size="sm"
            className="d-flex align-items-center"
            onClick={() => handleDeleteClick(props.data)}
            title="Delete Particle account"
        >
            <Trash2 size={14} />
        </Button >
      </div>
    );
  };

  const deviceList = (props) => {
    if (!props.data.devices || props.data.devices.length === 0) return <span className="text-muted small">No devices</span>;
    
    // Show count if too many, otherwise list first few
    const count = props.data.devices.length;
    return (
        <span className="badge bg-light text-dark border">
            {count} Device{count !== 1 ? 's' : ''}
        </span>
    );
  };

  // --- Grid Config ---

  const columnDefs = [
    { headerName: 'Particle Account', field: 'username', minWidth: 250, flex: 2 },
    { headerName: 'Product', field: 'product', width: 100 },
    { headerName: 'Devices', field: 'devices', cellRenderer: deviceList, flex: 1 },
    { headerName: 'Actions', field: 'action', cellRenderer: ActionButtons, minWidth: 360, flex: 1 }
  ];

  const gridOptions = {
    columnDefs: columnDefs,
    defaultColDef: {
      sortable: true,
      resizable: true,
      filter: true,
    },
    pagination: true,
    paginationPageSize: 10,
    rowHeight: 50, // Slightly taller for buttons
  };

  return (
    <div className="d-flex flex-column h-100">
      
      {/* 1. Search Bar Area */}
      <div className="mb-3">
        <InputGroup>
            <InputGroup.Text className="bg-white border-end-0">
                <Search size={18} className="text-muted" />
            </InputGroup.Text>
            <Form.Control 
                placeholder="Search Particle accounts by email..."
                className="border-start-0"
                value={quickFilterText}
                onChange={onFilterTextChange}
            />
        </InputGroup>
      </div>

      {/* 2. The Grid */}
      <div style={{ height: 600, width: '100%' }} className="ag-theme-alpine shadow-sm rounded overflow-hidden" >
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          gridOptions={gridOptions}
        >
        </AgGridReact>
      </div>

      {/* 3. Delete Confirmation Modal */}
      <Modal show={deleteModalShow} onHide={() => setDeleteModalShow(false)} centered>
        <Modal.Header closeButton className="bg-danger text-white">
          <Modal.Title>Delete Particle Account</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            Are you sure you want to permanently delete the Particle account <strong>{selectedUser?.username}</strong> and disable its dashboard login?
            <br/><br/>
            <span className="text-danger small">This action cannot be undone. Assigned devices may become unclaimed and can be reclaimed by someone who still has the physical device.</span>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteModalShow(false)}>Cancel</Button>
          <Button variant="danger" onClick={() => {
            setDeleteModalShow(false);
            ParticleService.deleteUser(selectedUser.username).then((result) => {
              if (result.error) {
                toast.error(result.error);
              } else {
                toast.success("User deleted");
              }
              fetchTableData();
            });
          }}>
            Delete User
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 4. New Device Manager Modal */}
      <DeviceManagerModal 
        show={manageModalShow}
        onHide={() => setManageModalShow(false)}
        user={selectedUser}
      />
      
    </div>
  );
}
